import { useCallback, useState } from 'react';

import type { parseHtml } from '@change-corgi/core-react/html';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useKnownAvailableParams } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import type { SocialStarterDraftData } from '#src/app/pages/socialStarter/socialStarterDraft/pageContext';
import { useSocialStarterPageData } from '#src/app/pages/socialStarter/socialStarterDraft/pageContext';
import { useClaimPetition } from '#src/app/shared/hooks/socialStarter';
import { normalizeHandle } from '#src/app/shared/utils/socialMedia';

type UseSocialStarterLayoutReturn = ModelHookResult<
	{
		draft: SocialStarterDraftData;
		authorName: string | null;
		authorHandle: string | null;
		authorAvatarUrl: string | null;
		platform: string | null;
		videoCoverUrl: string | null;
		caption: string | null;
		heading: ReturnType<typeof parseHtml>;
		subheading: string;
		loading: boolean;
		error: Error | null;
		videoConsentCheckboxEnabled: boolean;
		videoConsent: boolean;
	},
	{
		claimFromTop: () => void;
		claimFromBottom: () => void;
		setVideoConsent: (videoConsent: boolean) => void;
	}
>;

/**
 * Drives the v3 Social Starter landing page.
 *
 * Token-backed page data is used, but only the first (recommended) draft is
 * exposed. The author handle is normalized so callers never have to worry
 * about a leading `@`. Both CTAs delegate to `useClaimPetition`, which creates
 * a `user_draft` SAP draft so the next screen lands on the editable input
 * fields (`/start-a-petition/create`). The two `claim*` actions exist solely
 * so analytics can distinguish which CTA the user pressed.
 */
export function useSocialStarterLayout(): UseSocialStarterLayoutReturn {
	const {
		data: {
			drafts,
			name,
			username,
			platform,
			avatarUrl,
			videoCoverUrl,
			caption,
			imagePrewarmEnabled,
			videoConsentCheckboxEnabled,
			token: resolvedToken,
			sourceLocation,
		},
	} = useSocialStarterPageData();
	const { translate, translateHtml } = useI18n();
	const track = useTracking();
	// The raw route param (which may be a vanity slug rather than the real token) is used to
	// build the default return path back to this page — kept separate from the page data's
	// resolved token, which is what identifies the angle to SAP.
	const { token: routeToken } = useKnownAvailableParams<{ token: string }>();

	const draft = drafts[0];
	const cleanedHandle = normalizeHandle(username);

	// `translateHtml` is used so the personalized heading can render the handle
	// in the interactive blue color via inline HTML in the translation.
	const heading = cleanedHandle
		? translateHtml('corgi.social_starter.hero.heading.personalized', { handle: cleanedHandle })
		: translateHtml('corgi.social_starter.hero.heading.generic');
	const subheading = translate('corgi.social_starter.hero.subheading');

	// Checked by default; not read from any persisted source. Read fresh on every mount, so
	// re-entering the landing page (e.g. via browser back) always resets it.
	const [videoConsent, setVideoConsent] = useState(true);

	const { data: claimData, actions: claimActions } = useClaimPetition({
		draft,
		prewarmImageDraft: imagePrewarmEnabled ? draft : undefined,
		videoConsent,
		pageData: { username, token: resolvedToken, sourceLocation },
		returnPath: `/social/${encodeURIComponent(routeToken)}`,
	});

	const claimFromTop = useCallback(() => {
		void track('sap_social_starter_main_cta_click', { location: 'top' });
		claimActions.startPetition();
	}, [claimActions, track]);

	const claimFromBottom = useCallback(() => {
		void track('sap_social_starter_main_cta_click', { location: 'bottom' });
		claimActions.startPetition();
	}, [claimActions, track]);

	return {
		data: {
			draft,
			authorName: name,
			authorHandle: cleanedHandle,
			authorAvatarUrl: avatarUrl,
			platform,
			videoCoverUrl,
			caption,
			heading,
			subheading,
			loading: claimData.loading,
			error: claimData.error,
			videoConsentCheckboxEnabled,
			videoConsent,
		},
		actions: { claimFromTop, claimFromBottom, setVideoConsent },
	};
}
